import { useState } from 'react';

import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

import { usePaymentDetails } from '../../hooks/payment-details/use-payment-details';
import { PaymentDetailsEntry } from '../../types/payment-details';
import { DeletePaymentDetailsModal } from './delete-payment-details-modal';
import { PaymentDetailsFormModal } from './payment-details-form-modal';
import { PaymentDetailsTable } from './payment-details-table';

const I18N = 'pages.finance.paymentDetails';

/**
 * Self-managed Payment Details card on the Finance settings page. CRUD happens
 * through modals with immediate per-action saves — it never touches the settings
 * outlet footer (that stays wired to the rate-configuration form above it).
 */
export const PaymentDetailsCard = () => {
  const { t } = useTranslation('authenticated.settings');

  const { data, isLoading, error, refetch } = usePaymentDetails();
  const entries = data ?? [];
  const total = entries.length;
  const hasNoDefault = total > 0 && !entries.some(entry => entry.isDefault);

  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<PaymentDetailsEntry | null>(null);
  const [deletingEntry, setDeletingEntry] = useState<PaymentDetailsEntry | null>(null);

  const openCreateModal = () => {
    setEditingEntry(null);
    setIsFormModalOpen(true);
  };

  const openEditModal = (entry: PaymentDetailsEntry) => {
    setEditingEntry(entry);
    setIsFormModalOpen(true);
  };

  const closeFormModal = () => {
    setIsFormModalOpen(false);
    setEditingEntry(null);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t(`${I18N}.card.title`)}</CardTitle>
        <CardDescription>{t(`${I18N}.card.description`)}</CardDescription>
      </CardHeader>
      <CardContent>
        {hasNoDefault && (
          <Alert variant="warning" className="mb-4">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{t(`${I18N}.card.noDefaultWarning`)}</AlertDescription>
          </Alert>
        )}

        {total > 0 && (
          <p className="mb-4 text-xs text-muted-foreground">
            {t(`${I18N}.card.countLine`, { count: total })}
          </p>
        )}

        <PaymentDetailsTable
          entries={entries}
          isLoading={isLoading}
          error={error}
          onRetry={() => {
            void refetch();
          }}
          onEdit={openEditModal}
          onDelete={setDeletingEntry}
          onAdd={openCreateModal}
        />

        <PaymentDetailsFormModal
          open={isFormModalOpen}
          onClose={closeFormModal}
          entry={editingEntry}
          existingEntries={entries}
        />

        <DeletePaymentDetailsModal
          entry={deletingEntry}
          open={deletingEntry !== null}
          onClose={() => setDeletingEntry(null)}
          totalCount={total}
        />
      </CardContent>
    </Card>
  );
};
